import { z } from "zod";
import { auditInfoSchema } from "../common/schemas";

export const assetTypes = [
    "Laptop",
    "Desktop",
    "Monitor",
    "Phone",
    "Headset",
    "Dock",
    "Keyboard",
    "Mouse",
    "Webcam",
    "Networking",
    "Conferencing Device",
    "Printer",
    "Other",
] as const;
export type AssetType = (typeof assetTypes)[number];

/**
 * Canonicalize an arbitrary asset-type string to the PascalCase form
 * the rest of the frontend speaks. The backend serializes enums with
 * `JsonStringEnumConverter(JsonNamingPolicy.CamelCase)`, so the wire
 * format is lowercase ("laptop", "headset"); without this normalizer
 * a lowercase value reaching the form's `<Select value="laptop">`
 * doesn't match any of the PascalCase options and the field renders
 * blank. The save side already works because the API's `TryParse` is
 * case-insensitive; the bug was purely on display.
 *
 * Unrecognized values pass through untouched so the user can see
 * (and correct) anything outside the enum set instead of having it
 * silently swallowed — also gives the AI photo recognizer headroom
 * to suggest something we don't model yet.
 */
export function canonicalAssetType(raw: string): string {
    const match = assetTypes.find((t) => t.toLowerCase() === raw.toLowerCase());
    return match ?? raw;
}

export const assignmentActions = ["Preserve", "Assign", "Release"] as const;
export type AssignmentAction = (typeof assignmentActions)[number];

export function canonicalAssignmentAction(raw: string): string {
    const match = assignmentActions.find((t) => t.toLowerCase() === raw.toLowerCase());
    return match ?? raw;
}

export const assetViewSchema = z.object({
    id: z.string(),
    // Nullable to accommodate legacy rows persisted before sageOfficeId
    // became required on the Asset aggregate. The wire payload for those
    // rows carries `null` (the C# property defaults to `null!` when the
    // JSON property is absent in storage), and a strict `z.string()`
    // here would zod-throw the entire `/api/assets` response and crash
    // the list page for an entire tenant if even one legacy asset is
    // present. The form/save side stays strict — `AssetUpsertPayload`
    // still requires a non-empty `sageOfficeId` and `AssetForm.validate`
    // forces the user to pick one before save — so the only place
    // `null` ever exists is on a freshly-loaded legacy record, and the
    // list renders a visible "No office" badge so those records are
    // easy to find and fix. Once the data is backfilled this can be
    // tightened back to `z.string()`.
    sageOfficeId: z.string().nullable(),
    type: z.string().transform(canonicalAssetType),
    make: z.string(),
    model: z.string(),
    serialNumber: z.string(),
    assetTag: z.string().nullable().optional(),
    // Acquisition metadata. Both fields are optional on the wire — the
    // server emits `null` when unset and a value when set. Date arrives
    // as an ISO `"YYYY-MM-DD"` string because that's how System.Text.Json
    // serializes `DateOnly`. Price is a plain number (tenant default
    // currency); keep it numeric here so list/detail views can format it
    // without re-parsing.
    purchaseDate: z.string().nullable().optional(),
    purchasePrice: z.number().nullable().optional(),
    currentAssignmentId: z.string().nullable().optional(),
    created: auditInfoSchema.nullable().optional(),
    modified: auditInfoSchema.nullable().optional(),
    deleted: auditInfoSchema.nullable().optional(),
    isDeleted: z.boolean(),
});
export type AssetView = z.infer<typeof assetViewSchema>;

export const assetListResponseSchema = z.object({
    results: z.array(assetViewSchema),
});

export const assetMutationResponseSchema = z.object({
    asset: assetViewSchema,
});

/**
 * Discriminated target for the AssetForm's optional inline "Assign to"
 * picker. Either flavor opts the page-level save handler into the
 * matching atomic endpoint:
 *   - kind: "Workspace" → POST /api/workspaces/{id}/assets
 *   - kind: "User"      → POST /api/users/{id}/assets
 *
 * Kept structurally identical (only the `kind` literal differs) so
 * routing logic in the page handlers is a single switch.
 */
export type AssetAssignTarget =
    | { readonly kind: "Workspace"; readonly id: string }
    | { readonly kind: "User"; readonly id: string };

export interface AssetUpsertPayload {
    readonly id: string;
    readonly sageOfficeId: string;
    readonly type: string;
    readonly make: string;
    readonly model: string;
    readonly serialNumber: string;
    readonly assetTag: string | null;
    /**
     * ISO `"YYYY-MM-DD"` calendar date when the asset was purchased.
     * `null` means "not recorded". The HTML `<input type="date">`
     * native format aligns 1:1 with the wire format, so there is no
     * timezone conversion to worry about — a date is a date.
     */
    readonly purchaseDate: string | null;
    /**
     * Price paid for the asset in the tenant's default currency. `null`
     * means "not recorded". Non-negative when present.
     */
    readonly purchasePrice: number | null;
    /**
     * When the AssetForm's optional inline "Assign to" picker has a
     * target selected, the form passes the {kind, id} pair here so the
     * containing page can route the save through the matching atomic
     * endpoint instead of the regular create. Always `undefined` for
     * edit-mode payloads.
     */
    readonly assignTo?: AssetAssignTarget;
}

// Photo-fill response contract — mirrors RecognizeAssetFromPhotoResponse
// in Galileo.Office.Contracts. The recognizer returns a best-effort
// suggestion (every field independently nullable) plus a flat list of
// every legible string it observed on the asset, used by PR #5's
// reassignment-chip UX.
export const recognitionConfidenceSchema = z.object({
    type: z.number(),
    make: z.number(),
    model: z.number(),
    serialNumber: z.number(),
    assetTag: z.number(),
});
export type RecognitionConfidence = z.infer<typeof recognitionConfidenceSchema>;

export const assetRecognitionSuggestionSchema = z.object({
    // AOAI returns lowercase enum values ("laptop"); normalize at the
    // schema boundary for the same reason as `assetViewSchema.type`.
    // `null` passes through unchanged — a missing suggestion is a
    // legitimate signal, not a bug to canonicalize away.
    type: z
        .string()
        .nullable()
        .transform((v) => (v === null ? null : canonicalAssetType(v))),
    make: z.string().nullable(),
    model: z.string().nullable(),
    serialNumber: z.string().nullable(),
    assetTag: z.string().nullable(),
    confidence: recognitionConfidenceSchema,
});
export type AssetRecognitionSuggestion = z.infer<typeof assetRecognitionSuggestionSchema>;

export const recognizedFieldSchema = z.object({
    value: z.string(),
    guessedAs: z.string().nullable(),
});
export type RecognizedField = z.infer<typeof recognizedFieldSchema>;

export const recognizeAssetFromPhotoResponseSchema = z.object({
    suggestion: assetRecognitionSuggestionSchema,
    observedTexts: z.array(recognizedFieldSchema),
});
export type RecognizeAssetFromPhotoResponse = z.infer<typeof recognizeAssetFromPhotoResponseSchema>;

// Tracks per-field AI provenance after a "Fill from photo" success: maps
// a form-field name to the recognizer's confidence score (0..1). Cleared
// for a field as soon as the user manually edits it. Used by AssetForm to
// render the "AI · 87%" badge below pre-filled fields.
export type AiFilledFields = Partial<Record<keyof AssetUpsertPayload, number>>;

// Sibling of AiFilledFields: tracks per-field BARCODE-SCAN provenance.
// Maps a form-field name to the detected barcode format (e.g. "code_128").
// Cleared for a field on manual edit. Powers the "Scanned" badge and
// keeps scan provenance from being conflated with AI provenance — both
// can apply to the same field across two fills, and the badge picks
// scan over AI because barcode > OCR for confidence.
export type ScanFilledFields = Partial<Record<keyof AssetUpsertPayload, string>>;

/**
 * Wire shape for one bulk-import row. The server reconciles by `id`
 * when that asset already exists, otherwise by unique `serialNumber`,
 * so re-uploading the same CSV (even with freshly generated ids) will
 * not create duplicates. `assignmentAction` makes blank assignment
 * columns unambiguous on an existing asset.
 */
export interface BulkAssetRow {
    /**
     * Original spreadsheet line number. Sent explicitly because blank
     * CSV lines are omitted from the request, so the API cannot recover
     * this value from the item's array position.
     */
    readonly sourceRowIndex: number;
    readonly id: string;
    readonly sageOfficeId: string;
    readonly type: string;
    readonly make: string;
    readonly model: string;
    readonly serialNumber: string;
    readonly assetTag: string | null;
    readonly purchaseDate: string | null;
    readonly purchasePrice: number | null;
    readonly assignmentAction: AssignmentAction | string;
    /**
     * Workspace to assign when `assignmentAction` is Assign.
     * Mutually exclusive with `assignedToUserId`.
     */
    readonly assignedToWorkspaceId: string | null;
    /**
     * HR-user to assign when `assignmentAction` is Assign.
     * Mutually exclusive with `assignedToWorkspaceId`.
     */
    readonly assignedToUserId: string | null;
    /**
     * Client-generated assignment UUID, set when action is Assign so a
     * retry of the same parsed table replays the same assignment id.
     * `null` for Preserve/Release.
     */
    readonly assignmentId: string | null;
}

export interface BulkImportAssetsPayload {
    readonly items: readonly BulkAssetRow[];
}

export const bulkAssetRowResultSchema = z.object({
    rowIndex: z.number(),
    outcome: z.enum(["created", "updated", "skipped", "failed"]),
    errorMessage: z.string().nullable().optional(),
    id: z.string(),
});
export type BulkAssetRowResult = z.infer<typeof bulkAssetRowResultSchema>;

export const bulkImportAssetsResponseSchema = z.object({
    totalRows: z.number(),
    created: z.number(),
    updated: z.number(),
    skipped: z.number(),
    failed: z.number(),
    results: z.array(bulkAssetRowResultSchema),
});
export type BulkImportAssetsResult = z.infer<typeof bulkImportAssetsResponseSchema>;

/**
 * Mirror of the backend hard cap. Held here so the SPA can refuse an
 * over-large file at parse time and tell the user up-front rather than
 * letting them upload, wait, and then receive a 400.
 */
export const BULK_IMPORT_ASSETS_MAX_ROWS = 1000;
